import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, ChevronUp, ChevronDown, Trash2, Scissors, ArrowUpFromLine, Plus, Minus,
  Save, History, CheckCircle2, AlertTriangle, X,
} from "lucide-react";

let _seq = 0;
const uid = () => `r${Date.now()}_${_seq++}`;

function ImpactGrid({ impact }) {
  if (!impact) return null;
  const rows = Object.entries(impact).filter(([, v]) => typeof v === "number");
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="edit-impact">
      {rows.map(([k, v]) => (
        <div key={k} className="rounded-md border border-border bg-[#0d1016] px-2.5 py-1.5">
          <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
          <div className="font-mono text-sm text-slate-200">{v}</div>
        </div>
      ))}
    </div>
  );
}

function ChangeReportChips({ report }) {
  if (!report) return null;
  return (
    <div className="grid grid-cols-4 gap-2">
      {[["Unchanged", report.unchanged], ["Added", report.added], ["Deleted", report.deleted], ["Modified", report.modified]].map(([l, v]) => (
        <div key={l} className="rounded-md border border-border bg-[#0d1016] px-2 py-1.5 text-center">
          <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{l}</div>
          <div className={`font-mono text-sm ${l !== "Unchanged" && v > 0 ? "text-amber-300" : "text-slate-200"}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function RevisionsDialog({ projectId, chapterId, open, onClose }) {
  const [versions, setVersions] = useState([]);
  const [diff, setDiff] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get(`/projects/${projectId}/chapters/${chapterId}/versions`)
      .then((r) => setVersions(r.data.versions)).catch(() => setVersions([]));
    setDiff(null);
  }, [open, projectId, chapterId]);

  const compare = async (vid) => {
    const active = versions.find((v) => v.active);
    if (!active) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/chapters/${chapterId}/versions/${vid}/diff?to=${active.id}`);
      setDiff(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border-border bg-[#12161f] text-slate-100" data-testid="revisions-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Revision history</DialogTitle>
          <DialogDescription className="text-slate-400">Every save is preserved. Compare any earlier revision against the active one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {versions.map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded-lg border border-border bg-slate-950/40 px-4 py-2.5" data-testid={`revision-${v.version_number}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-slate-200">v{v.version_number}</span>
                  <span className="rounded bg-slate-700/40 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-slate-300">{v.source}</span>
                  {v.active && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-500/30">active</span>}
                </div>
                <div className="mt-0.5 font-mono text-[11px] text-slate-500">{(v.word_count ?? 0).toLocaleString()} w · {v.paragraph_count} p · {v.created_at ? new Date(v.created_at).toLocaleString() : ""}</div>
              </div>
              {!v.active && (
                <Button size="sm" variant="outline" data-testid={`compare-revision-${v.version_number}`} onClick={() => compare(v.id)}
                  className="border-amber-500/30 text-amber-200 hover:bg-amber-500/10">Compare</Button>
              )}
            </div>
          ))}
        </div>
        {diff && (
          <div className="mt-2 space-y-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3" data-testid="revision-diff">
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">v{diff.from.version_number} → v{diff.to.version_number} (active)</div>
            <ChangeReportChips report={diff.report} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ChapterNarrationEditor({ projectId, chapterId, chapterTitle, onClose, onCommitted }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showRevisions, setShowRevisions] = useState(false);
  const taRefs = useRef({});

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/chapters/${chapterId}/paragraphs`);
    const rs = (data.paragraphs || [])
      .filter((p) => p.kind !== "heading")
      .map((p) => ({ key: uid(), kind: p.kind === "body" ? "body" : "scene", text: p.text || "", src_order: p.order }));
    setRows(rs.length ? rs : [{ key: uid(), kind: "body", text: "", src_order: null }]);
  }, [projectId, chapterId]);
  useEffect(() => { load(); }, [load]);

  const setText = (key, text) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, text } : r)));
  const move = (idx, dir) => setRows((rs) => {
    const j = idx + dir; if (j < 0 || j >= rs.length) return rs;
    const a = [...rs]; [a[idx], a[j]] = [a[j], a[idx]]; return a;
  });
  const del = (key) => setRows((rs) => (rs.length <= 1 ? rs : rs.filter((r) => r.key !== key)));
  const addBelow = (idx) => setRows((rs) => {
    const a = [...rs]; a.splice(idx + 1, 0, { key: uid(), kind: "body", text: "", src_order: null }); return a;
  });
  const addSceneBelow = (idx) => setRows((rs) => {
    const a = [...rs]; a.splice(idx + 1, 0, { key: uid(), kind: "scene", text: "", src_order: null }); return a;
  });
  const mergeUp = (idx) => setRows((rs) => {
    if (idx === 0) return rs;
    let p = idx - 1;
    while (p >= 0 && rs[p].kind !== "body") p--;
    if (p < 0) return rs;
    const a = [...rs];
    a[p] = { ...a[p], text: `${a[p].text.trim()} ${a[idx].text.trim()}`.trim() };
    a.splice(idx, 1); return a;
  });
  const splitAt = (idx, key) => setRows((rs) => {
    const el = taRefs.current[key];
    const text = rs[idx].text;
    let pos = el && el.selectionStart > 0 && el.selectionStart < text.length ? el.selectionStart : Math.floor(text.length / 2);
    const a = [...rs];
    const first = text.slice(0, pos).trim();
    const second = text.slice(pos).trim();
    a[idx] = { ...a[idx], text: first };
    a.splice(idx + 1, 0, { key: uid(), kind: "body", text: second, src_order: rs[idx].src_order });
    return a;
  });

  const buildPayload = (confirm) => ({
    confirm,
    paragraphs: rows.map((r) => ({ kind: r.kind === "scene" ? "blank" : "body", text: r.text, src_order: r.src_order })),
  });

  const review = async () => {
    if (!rows.some((r) => r.kind === "body" && r.text.trim())) { toast.error("Add at least one paragraph of narration text."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${chapterId}/edit`, buildPayload(false));
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${chapterId}/edit`, buildPayload(true));
      toast.success(`Saved v${data.version_number} — ${data.carried_takes} take(s) carried, ${data.stale_takes_kept} kept & flagged stale.`);
      setPreview(null);
      if (onCommitted) await onCommitted();
      onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!rows) return (
    <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
  );

  return (
    <div data-testid="narration-editor">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.05] p-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-amber-300/80">Editing narration · {chapterTitle}</div>
          <p className="mt-0.5 text-[11px] text-slate-400">Edit, split, merge, reorder or add paragraphs. The original DOCX is untouched. Changed passages keep their audio but are flagged stale.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" data-testid="editor-revisions" onClick={() => setShowRevisions(true)} className="gap-1 border-border text-slate-300 hover:bg-slate-800/50"><History className="h-3.5 w-3.5" /> Revisions</Button>
          <Button size="sm" variant="outline" data-testid="editor-cancel" onClick={onClose} className="gap-1 border-border text-slate-300 hover:bg-slate-800/50"><X className="h-3.5 w-3.5" /> Close</Button>
          <Button size="sm" data-testid="editor-review" onClick={review} disabled={busy} className="gap-1 bg-amber-500 text-slate-950 hover:bg-amber-400">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Review &amp; save
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((r, idx) => (
          r.kind === "scene" ? (
            <div key={r.key} data-testid={`editor-scene-${idx}`} className="flex items-center gap-3 rounded-md border border-dashed border-amber-500/30 bg-[#0f1217] px-3 py-2">
              <span className="flex-1 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500/60">◆ scene break ◆</span>
              <div className="flex items-center gap-1">
                <button data-testid={`editor-up-${idx}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="text-slate-500 hover:text-amber-300 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                <button data-testid={`editor-down-${idx}`} disabled={idx === rows.length - 1} onClick={() => move(idx, 1)} className="text-slate-500 hover:text-amber-300 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                <button data-testid={`editor-del-${idx}`} onClick={() => del(r.key)} className="text-slate-500 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ) : (
            <div key={r.key} data-testid={`editor-row-${idx}`} className="rounded-md border border-border bg-[#12161f] p-2.5">
              <div className="flex items-start gap-2">
                <span className="mt-2 font-mono text-[10px] text-slate-600 w-6 shrink-0 text-right">{idx + 1}</span>
                <Textarea
                  ref={(el) => { taRefs.current[r.key] = el; }}
                  data-testid={`editor-text-${idx}`}
                  value={r.text}
                  onChange={(e) => setText(r.key, e.target.value)}
                  rows={Math.min(8, Math.max(2, Math.ceil((r.text.length || 1) / 80)))}
                  className="resize-y bg-[#0b0e14] font-serif text-[15px] leading-relaxed text-slate-200"
                />
                <div className="flex shrink-0 flex-col items-center gap-1">
                  <button data-testid={`editor-up-${idx}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="text-slate-500 hover:text-amber-300 disabled:opacity-30" title="Move up"><ChevronUp className="h-4 w-4" /></button>
                  <button data-testid={`editor-down-${idx}`} disabled={idx === rows.length - 1} onClick={() => move(idx, 1)} className="text-slate-500 hover:text-amber-300 disabled:opacity-30" title="Move down"><ChevronDown className="h-4 w-4" /></button>
                  <button data-testid={`editor-split-${idx}`} onClick={() => splitAt(idx, r.key)} className="text-slate-500 hover:text-teal-300" title="Split at cursor"><Scissors className="h-4 w-4" /></button>
                  <button data-testid={`editor-merge-${idx}`} disabled={idx === 0} onClick={() => mergeUp(idx)} className="text-slate-500 hover:text-teal-300 disabled:opacity-30" title="Merge into previous"><ArrowUpFromLine className="h-4 w-4" /></button>
                  <button data-testid={`editor-del-${idx}`} onClick={() => del(r.key)} className="text-slate-500 hover:text-red-300" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-1 flex items-center justify-end gap-3 pr-9">
                <button data-testid={`editor-add-scene-${idx}`} onClick={() => addSceneBelow(idx)} className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-amber-300"><Minus className="h-3 w-3" /> scene break below</button>
                <button data-testid={`editor-add-${idx}`} onClick={() => addBelow(idx)} className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-amber-300"><Plus className="h-3 w-3" /> paragraph below</button>
              </div>
            </div>
          )
        ))}
      </div>

      {/* Impact / confirm dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg border-amber-500/15 bg-[#12161f] text-slate-100" data-testid="edit-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Audio-impact report</DialogTitle>
            <DialogDescription className="text-slate-400">{preview?.note}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm ${preview?.change_report?.no_changes ? "bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30" : "bg-amber-500/10 text-amber-300 ring-1 ring-amber-500/30"}`}>
              {preview?.change_report?.no_changes ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {preview?.change_report?.no_changes ? "No spoken paragraphs changed." : "Paragraph changes detected."}
            </div>
            <ChangeReportChips report={preview?.change_report} />
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Passage / take impact</div>
            <ImpactGrid impact={preview?.impact} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} className="border-border text-slate-300">Cancel</Button>
            <Button data-testid="confirm-edit" disabled={busy} onClick={commit} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save new revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RevisionsDialog projectId={projectId} chapterId={chapterId} open={showRevisions} onClose={() => setShowRevisions(false)} />
    </div>
  );
}

export default ChapterNarrationEditor;
